import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  TurnAnalysisTurn,
  AgentAssistCardData,
  StateActionOrderedItem,
  StateEntityFullItem,
} from '../types/api';
import {
  buildAssistTimeline,
  canonicalStepKeyFromOrdered,
  filterHeadsUpItems,
  filterOrderedForLiveLayout,
  isDialogueFocusStep,
  isMissedTypeViolation,
  itemStatusToLiveStatus,
  openStepKeysInOrdered,
  orderedRowKeyFromItem,
  promoteFocusStepTimelineStatus,
  normalizedScriptConstraints,
  scriptConstraintChipMeta,
  timelineRichFieldsFromOrdered,
  type LiveAssistTimelineAction,
} from '../utils/agentAssistLiveTimeline';
import { serializeEntityValue } from '../utils/entityValue';
import '../styles/components/AgentAssistLiveCard.css';
import '../styles/components/LiveAssistCard.css';
import EntityValueCell from './EntityValueCell';

export type AgentAssistLiveHeroVariant = 'default' | 'compact';

export interface AgentAssistLiveCardProps {
  allTurns: TurnAnalysisTurn[];
  currentTurnNumber: number;
  /** Focus-step layout: default = existing hero; compact = stacked subflow/title + minimal lines. */
  heroVariant?: AgentAssistLiveHeroVariant;
}

const formatStepLabel = (step?: string) =>
  (step || '')
    .split('_')
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ') || '—';

function stepKeyFromOrdered(item: StateActionOrderedItem): string {
  return (item.step_id ?? item.canonical_action_name ?? item.step_text ?? '').trim();
}

/** Pending pile tile: exclude completed, failed, and not_started. */
const ORDERED_PENDING_PILE_EXCLUDED_STATUSES = new Set(['completed', 'failed', 'not_started']);

function isOrderedPendingPileItem(item: StateActionOrderedItem | undefined): boolean {
  if (!item) return false;
  const s = (item.status ?? '').toLowerCase();
  return !ORDERED_PENDING_PILE_EXCLUDED_STATUSES.has(s);
}

/** Build a timeline row for a ta_turn_action entry when the section snapshot has no match. */
function timelineActionFromOrderedItem(
  item: StateActionOrderedItem,
  opts: { focusStepId: string; isPastState: boolean }
): LiveAssistTimelineAction | null {
  const stepName = stepKeyFromOrdered(item);
  if (!stepName) return null;
  const rowKey = orderedRowKeyFromItem(item);
  const mapped = itemStatusToLiveStatus(item, [], opts.isPastState);
  const status = promoteFocusStepTimelineStatus(mapped, item, {
    focusStepId: opts.focusStepId,
    isLastSection: true,
  });
  const displayTitle =
    (item.step_title && item.step_title.trim()) || formatStepLabel(stepName);
  const sf = (item.subflow_name ?? '').trim();
  const scriptConstraints = normalizedScriptConstraints(item.constraints ?? undefined);
  const rich = timelineRichFieldsFromOrdered(item);
  return {
    key: `db-${rowKey}`,
    step: stepName,
    rowKey,
    displayTitle,
    subflowName: sf || undefined,
    scriptConstraints: scriptConstraints.length > 0 ? scriptConstraints : undefined,
    primaryEntityName: item.primary_entity_name ?? undefined,
    status,
    remark: item.badge ?? undefined,
    stepSubtitle: rich.stepSubtitle,
    assistText: rich.assistText,
    headsUp: rich.headsUp.length > 0 ? rich.headsUp : undefined,
  };
}

/** Upcoming order matches persisted script order (flatten_ordered_steps / order_in_state). */
function comparePendingOrderedItems(a: StateActionOrderedItem, b: StateActionOrderedItem): number {
  const oa = a.order_in_state ?? 1e9;
  const ob = b.order_in_state ?? 1e9;
  if (oa !== ob) return oa - ob;
  return stepKeyFromOrdered(a).localeCompare(stepKeyFromOrdered(b), undefined, { sensitivity: 'base' });
}

/** Upcoming: every ta_turn_action row for this state (script order), except Done + Focus. */
function buildSortedRemainingPairs(
  ordered: StateActionOrderedItem[] | undefined,
  actionByRowKey: Map<string, LiveAssistTimelineAction>,
  heroRowKeys: Set<string>,
  opts: { focusStepId: string; isPastState: boolean }
): Array<{ item: StateActionOrderedItem; action: LiveAssistTimelineAction }> {
  if (!ordered?.length) return [];
  const pairs: Array<{ item: StateActionOrderedItem; action: LiveAssistTimelineAction }> = [];
  for (const item of ordered) {
    const rk = orderedRowKeyFromItem(item);
    if (heroRowKeys.has(rk)) continue;
    const st = (item.status ?? '').toLowerCase();
    if (st === 'completed' || st === 'failed') continue;
    const nm = stepKeyFromOrdered(item);
    if (!nm) continue;
    let a = actionByRowKey.get(rk);
    if (!a) {
      a = timelineActionFromOrderedItem(item, opts) ?? undefined;
      if (a) actionByRowKey.set(rk, a);
    }
    if (!a) continue;
    pairs.push({ item, action: a });
  }
  pairs.sort((p, q) => comparePendingOrderedItems(p.item, q.item));
  return pairs;
}

/** Customer spoke; agent must address (question, objection, etc.). */
function isHandleCustomerResponse(item: StateActionOrderedItem | undefined): boolean {
  if (!item) return false;
  return (item.status ?? '').toLowerCase() === 'handle_customer_response';
}

/** Steps waiting on the customer or on explicit confirmation (same open semantics as engine). */
function isAwaitingCustomerResponse(item: StateActionOrderedItem | undefined): boolean {
  if (!item) return false;
  const s = (item.status ?? '').toLowerCase();
  return s === 'awaiting_customer_response' || s === 'awaiting_confirmation';
}

/** Hero-eligible open customer-interaction steps (handle takes priority over awaiting). */
function isCustomerInteractionHero(item: StateActionOrderedItem | undefined): boolean {
  return isHandleCustomerResponse(item) || isAwaitingCustomerResponse(item);
}

/** In-focus step marker — same dot as timeline live; pulse only when awaiting customer. */
function FocusStepLiveIndicator({
  action,
  orderedItem,
}: {
  action: LiveAssistTimelineAction;
  orderedItem: StateActionOrderedItem | undefined;
}) {
  if (action.status.startsWith('missed') || action.status.startsWith('skipped_')) {
    return null;
  }
  const awaiting =
    isAwaitingCustomerResponse(orderedItem) || isHandleCustomerResponse(orderedItem);
  return (
    <span
      className={`aal-live-dot${awaiting ? ' aal-live-dot--awaiting' : ''}`}
      aria-hidden
    />
  );
}

/** Open customer-interaction heroes (handle first, then awaiting); sorted by script order. */
function buildCustomerInteractionHeroActions(
  ordered: StateActionOrderedItem[] | undefined,
  actionByRowKey: Map<string, LiveAssistTimelineAction>
): LiveAssistTimelineAction[] {
  if (!ordered?.length) return [];
  const hits: Array<{ item: StateActionOrderedItem; action: LiveAssistTimelineAction }> = [];
  for (const item of ordered) {
    if (!isCustomerInteractionHero(item)) continue;
    const nm = stepKeyFromOrdered(item);
    if (!nm) continue;
    const a = actionByRowKey.get(orderedRowKeyFromItem(item));
    if (!a) continue;
    hits.push({ item, action: a });
  }
  hits.sort((p, q) => {
    const ph = isHandleCustomerResponse(p.item) ? 0 : 1;
    const qh = isHandleCustomerResponse(q.item) ? 0 : 1;
    if (ph !== qh) return ph - qh;
    const d = (p.item.order_in_state ?? 1e9) - (q.item.order_in_state ?? 1e9);
    if (d !== 0) return d;
    return stepKeyFromOrdered(p.item).localeCompare(stepKeyFromOrdered(q.item), undefined, {
      sensitivity: 'base',
    });
  });
  return hits.map((h) => h.action);
}

/** @deprecated Use buildCustomerInteractionHeroActions */
function buildAwaitingHeroActions(
  ordered: StateActionOrderedItem[] | undefined,
  actionByRowKey: Map<string, LiveAssistTimelineAction>
): LiveAssistTimelineAction[] {
  return buildCustomerInteractionHeroActions(ordered, actionByRowKey);
}

function entityStatusFromFull(item: StateEntityFullItem): string {
  const stage = (item.stage ?? '').toLowerCase();
  const s = (item.status ?? '').toLowerCase();
  const serialized = serializeEntityValue(item.value);
  const hasValue = item.filled ?? serialized != null;
  if (s === 'completed') return stage === 'ack' ? 'completed_ack' : 'completed';
  if (hasValue && s !== 'completed') {
    if (stage === 'confirm') return 'to_confirm';
    return 'incomplete_other';
  }
  if (s === 'to_repeat') return 'to_repeat';
  return 'not_filled';
}

function buildEntityRows(card: AgentAssistCardData | null): Array<{
  entity: string;
  value: string | null;
  status: string;
}> {
  if (!card?.state_entities_full?.length) return [];
  return card.state_entities_full
    .map((item) => ({
      entity: (item.entity_name ?? '').trim(),
      value: serializeEntityValue(item.value),
      status: entityStatusFromFull(item),
    }))
    .filter((r) => r.entity.length > 0);
}

function sortEntitiesByRecency(
  allTurns: TurnAnalysisTurn[],
  currentTurnNumber: number,
  rows: Array<{ entity: string; value: string | null; status: string }>
): typeof rows {
  const lastTurn = new Map<string, number>();
  const prevVal = new Map<string, string>();
  for (const t of allTurns) {
    if (t.turn_number > currentTurnNumber) break;
    const full = t.agent_card?.state_entities_full ?? [];
    for (const e of full) {
      const name = (e.entity_name ?? '').trim();
      if (!name) continue;
      const val = serializeEntityValue(e.value) ?? '';
      const prev = prevVal.has(name) ? prevVal.get(name)! : null;
      const changed =
        e.is_changed === true || (prev !== null && prev !== val) || (prev === null && val.length > 0);
      if (changed) lastTurn.set(name, t.turn_number);
      prevVal.set(name, val);
    }
  }
  return [...rows].sort((a, b) => {
    const ta = lastTurn.get(a.entity) ?? 0;
    const tb = lastTurn.get(b.entity) ?? 0;
    if (tb !== ta) return tb - ta;
    return a.entity.localeCompare(b.entity, undefined, { sensitivity: 'base' });
  });
}

/**
 * Same strings as compact AgentAssistCard “ongoing” rows (full lines from guidance parser).
 * Falls back to raw `guidance` lines with “listen to customer” if `ongoing_steps` is empty.
 */
function gatherOngoingListenLines(card: AgentAssistCardData | null, currentTurn: TurnAnalysisTurn | null): string[] {
  const fromCard =
    card?.ongoing_steps?.map((o) => (o.step ?? '').trim()).filter((s) => s.length > 0) ?? [];
  if (fromCard.length > 0) return fromCard;
  const raw = currentTurn?.guidance ?? [];
  return raw
    .map((l) => (l ?? '').trim())
    .filter(
      (s) =>
        s.length > 0 &&
        !s.startsWith('[OK] ') &&
        !s.startsWith('Next:') &&
        !s.startsWith('Pending') &&
        s.toLowerCase().includes('listen to customer')
    );
}

/** Score how well an ongoing guidance line matches a workflow step id (snake_case). */
function scoreOngoingMatch(stepId: string, ongoingLine: string): number {
  const sid = stepId.toLowerCase();
  const ll = ongoingLine.toLowerCase();
  let score = 0;
  const tokens = sid.split(/[_-]+/).filter((t) => t.length >= 3);
  for (const t of tokens) {
    if (t.length >= 4 && ll.includes(t)) score += 1;
  }
  const normStep = sid.replace(/_/g, '');
  const normLine = ll.replace(/[^a-z0-9]/g, '');
  if (normStep.length >= 5 && normLine.includes(normStep)) score += 4;
  const label = formatStepLabel(stepId).toLowerCase();
  for (const w of label.split(/\s+/)) {
    if (w.length >= 4 && ll.includes(w)) score += 1;
  }
  return score;
}

/**
 * Assign ongoing lines to live hero steps (greedy by match score) so each awaiting step can show
 * the same “Asking for …, listen to customer” copy as the compact card.
 */
function assignOngoingToLiveSteps(
  liveHeroes: LiveAssistTimelineAction[],
  ongoingLines: string[]
): Map<string, string> {
  const map = new Map<string, string>();
  if (!ongoingLines.length || !liveHeroes.length) return map;

  const pool = [...ongoingLines];
  for (const h of liveHeroes) {
    let bestJ = -1;
    let bestScore = 0;
    for (let j = 0; j < pool.length; j++) {
      const sc = scoreOngoingMatch(h.step, pool[j]!);
      if (sc > bestScore) {
        bestScore = sc;
        bestJ = j;
      }
    }
    if (bestJ >= 0 && bestScore > 0) {
      map.set(h.step.toLowerCase(), pool[bestJ]!);
      pool.splice(bestJ, 1);
    }
  }
  for (const h of liveHeroes) {
    if (map.has(h.step.toLowerCase())) continue;
    if (pool.length) {
      map.set(h.step.toLowerCase(), pool.shift()!);
    } else if (ongoingLines.length === 1) {
      map.set(h.step.toLowerCase(), ongoingLines[0]!);
    }
  }
  return map;
}

function findOrderedItem(
  ordered: StateActionOrderedItem[] | undefined,
  stepName: string
): StateActionOrderedItem | undefined {
  if (!ordered) return undefined;
  const k = stepName.toLowerCase();
  return ordered.find((o) => stepKeyFromOrdered(o).toLowerCase() === k);
}

function findOrderedItemForAction(
  ordered: StateActionOrderedItem[] | undefined,
  action: LiveAssistTimelineAction
): StateActionOrderedItem | undefined {
  if (!ordered) return undefined;
  if (action.rowKey) {
    const byRow = ordered.find((o) => orderedRowKeyFromItem(o) === action.rowKey);
    if (byRow) return byRow;
  }
  return findOrderedItem(ordered, action.step);
}

/** Top-right subflow chip: title case, underscores → spaces. */
function subflowCornerLabelFromName(name: string | null | undefined): string | null {
  const raw = (name ?? '').trim();
  if (!raw) return null;
  return formatStepLabel(raw.replace(/_/g, ' '));
}

/** Hero top-right: persisted subflow context only (not pipeline source). */
function heroSubflowCornerLabel(item: StateActionOrderedItem | undefined): string | null {
  if (!item) return null;
  return subflowCornerLabelFromName(item.subflow_name);
}

function entityValueForName(card: AgentAssistCardData | null, entityName: string): string | null {
  const row = card?.state_entities_full?.find(
    (e) => (e.entity_name ?? '').trim().toLowerCase() === entityName.toLowerCase()
  );
  if (!row) return null;
  const v = row.value;
  if (v === undefined || v === null) return null;
  const t = String(v).trim();
  return t.length ? t : null;
}

function heroEntityRows(
  card: AgentAssistCardData | null,
  orderedItem: StateActionOrderedItem | undefined,
  stepName: string
): Array<{ name: string; value: string }> {
  const rows: Array<{ name: string; value: string }> = [];
  const primary = (orderedItem?.primary_entity_name ?? '').trim();
  if (primary) {
    const val = entityValueForName(card, primary);
    rows.push({ name: formatStepLabel(primary), value: val ?? '—' });
  }
  const pendingBlock = card?.pending_entities?.find(
    (p) => (p.step ?? '').toLowerCase() === stepName.toLowerCase()
  );
  const pendingNames = pendingBlock?.entities ?? [];
  for (const ent of pendingNames) {
    if (!ent || ent.toLowerCase() === primary.toLowerCase()) continue;
    const val = entityValueForName(card, ent);
    rows.push({ name: formatStepLabel(ent), value: val ?? '—' });
  }
  return rows;
}

function timelineIndicatorForAction(a: LiveAssistTimelineAction): string {
  if (a.status === 'completed_failed') return '✗';
  if (a.status.startsWith('completed')) return '✓';
  if (a.status === 'live') return '●';
  if (a.status === 'next') return '◎';
  if (a.status.startsWith('missed')) return '✗';
  if (a.status === 'skipped') return '○';
  return '○';
}

/** Skipped script steps (same state) + missed (after transition) — Missed pile. */
function countsTowardMissedPile(status: string): boolean {
  return status.startsWith('missed') || status === 'skipped' || status.startsWith('skipped_');
}

/** Last index in timeline order = most recent occurrence for pile scroll. */
function findLastTimelineIndex(
  actions: LiveAssistTimelineAction[],
  category: 'completed' | 'pending' | 'missed' | 'violations'
): number {
  let idx = -1;
  for (let i = 0; i < actions.length; i++) {
    const a = actions[i]!;
    if (category === 'violations') {
      if ((a.violations ?? []).some((v) => !isMissedTypeViolation(v))) idx = i;
    } else if (category === 'missed') {
      if (
        a.status.startsWith('missed') ||
        a.status === 'skipped' ||
        a.status.startsWith('skipped_')
      ) {
        idx = i;
      }
    } else if (category === 'pending') {
      if (
        a.status === 'pending' ||
        a.status === 'next' ||
        a.status === 'live' ||
        a.status.startsWith('missed') ||
        a.status.startsWith('skipped_')
      ) {
        idx = i;
      }
    } else if (a.status.startsWith('completed')) {
      idx = i;
    }
  }
  return idx;
}

/** One visible line; multiple card.violations cycle upward, no controls. */
function ViolationsTicker({ lines }: { lines: string[] }) {
  const [idx, setIdx] = useState(0);
  const n = lines.length;
  const sig = lines.join('\u0000');

  useEffect(() => {
    setIdx(0);
  }, [sig]);

  useEffect(() => {
    if (n <= 1) return;
    const id = window.setInterval(() => {
      setIdx((i) => (i + 1) % n);
    }, 4800);
    return () => window.clearInterval(id);
  }, [n, sig]);

  if (n === 0) return null;

  return (
    <div className="aal-violations-ticker" role="status" aria-live="polite">
      <span className="material-icons aal-violations-ticker-icon" aria-hidden>
        cancel
      </span>
      <div className="aal-violations-ticker-viewport">
        <div
          className="aal-violations-ticker-track"
          style={{
            transform: `translateY(calc(-${idx} * var(--aal-ticker-line-h)))`,
            transition: 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {lines.map((text, i) => (
            <div key={`${i}-${text.slice(0, 48)}`} className="aal-violations-ticker-line" title={text}>
              {text}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FocusHeroStepCompact({
  action: a,
  orderedItem: oi,
  ongoingByLiveStep,
  timelineIndex,
}: {
  action: LiveAssistTimelineAction;
  orderedItem: StateActionOrderedItem | undefined;
  ongoingByLiveStep: Map<string, string>;
  timelineIndex: number | undefined;
}) {
  const badge = heroSubflowCornerLabel(oi);
  const assistText = (oi?.assist_text ?? '').trim();
  const stepSubtitle = (oi?.step_subtitle ?? '').trim();
  const assistForGuidance = assistText && assistText !== stepSubtitle ? assistText : '';
  const generatedListen =
    a.status === 'live' ? ongoingByLiveStep.get(a.step.toLowerCase())?.trim() || '' : '';
  const assistLine = assistForGuidance || generatedListen;
  const headsUpItems =
    Array.isArray(oi?.heads_up) && oi
      ? oi.heads_up.filter(
          (h) => (h?.title ?? '').trim().length > 0 || (h?.hint ?? '').trim().length > 0
        )
      : [];
  const violations = a.violations ?? [];
  const titleText = formatStepLabel((a.displayTitle ?? a.step) || '');
  const hasFocusDot =
    !a.status.startsWith('missed') &&
    a.status !== 'skipped' &&
    !a.status.startsWith('skipped_');
  const bodyIndentClass = hasFocusDot
    ? ' aal-hero-compact-stack--indent-dot'
    : a.status.startsWith('missed')
      ? ' aal-hero-compact-stack--indent-missed'
      : a.status.startsWith('skipped_')
        ? ' aal-hero-compact-stack--indent-skipped'
        : '';

  return (
    <div
      data-aal-idx={timelineIndex !== undefined ? timelineIndex : undefined}
      className={`aal-hero aal-hero--compact status-${a.status}${
        badge ? ' aal-hero--compact-has-subflow' : ''
      }`}
    >
      {badge && (
        <div className="aal-step-subflow-badge aal-step-subflow-badge--left" title={badge}>
          {badge}
        </div>
      )}
      <div className={`aal-hero-compact-stack${bodyIndentClass}`}>
        <div className="aal-hero-compact-title">
          <FocusStepLiveIndicator action={a} orderedItem={oi} />
          <span className="aal-hero-compact-title-main">
            {a.status.startsWith('missed') && (
              <span className="aal-missed-indicator" title="Missed step">
                {timelineIndicatorForAction(a)}
              </span>
            )}
            {a.status.startsWith('skipped_') && (
              <span
                className="material-icons aal-skipped-warn-icon"
                title="Skipped — still recover in this state"
                aria-hidden
              >
                warning
              </span>
            )}
            {titleText}
          </span>
        </div>
        {stepSubtitle && <div className="aal-hero-compact-subtitle">{stepSubtitle}</div>}
        {assistLine && (
          <div className="aal-hero-compact-line aal-hero-compact-line--assist">
            <span className="material-icons aal-hero-compact-icon" aria-hidden>
              lightbulb_outline
            </span>
            <span className="aal-hero-compact-line-text">{assistLine}</span>
          </div>
        )}
        {headsUpItems.map((h, hi) => {
          const ht = (h.title ?? '').trim();
          const hh = (h.hint ?? '').trim();
          const tip = hh || ht || '';
          const label = ht || hh || 'Heads-up';
          return (
            <div
              key={hi}
              className="aal-hero-compact-line aal-hero-compact-line--heads-up"
              title={tip}
              aria-label={tip || label}
            >
              <span className="material-icons aal-hero-compact-icon" aria-hidden>
                info_outline
              </span>
              <span className="aal-hero-compact-line-text">{label}</span>
            </div>
          );
        })}
      </div>
      {violations.length > 0 && (
        <footer className="aal-hero-compact-footer" aria-label="Violations">
          <div className="live-assist-action-violations aal-hero-violations">
            {violations.map((v, vi) => (
              <div key={vi} className="live-assist-violation-line" title={v.message}>
                <span
                  className={`live-assist-violation-icon ${isMissedTypeViolation(v) ? 'live-assist-violation-icon-missed' : ''}`}
                  aria-hidden
                >
                  {isMissedTypeViolation(v) ? '✗' : '!'}
                </span>
                <span className="live-assist-violation-text">{v.violationType.replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        </footer>
      )}
    </div>
  );
}

function isTimelineCompletedStatus(status: string): boolean {
  return status.startsWith('completed');
}

/** Same rich lines as focus compact hero (subtitle / assist / heads-up). */
function TimelineStepRichBody({ action: a }: { action: LiveAssistTimelineAction }) {
  const completed = isTimelineCompletedStatus(a.status);
  const stepSubtitle = (a.stepSubtitle ?? '').trim();
  const assistText = (a.assistText ?? '').trim();
  const assistLine =
    !completed && assistText && assistText !== stepSubtitle ? assistText : '';
  const headsUpItems = completed ? [] : filterHeadsUpItems(a.headsUp);

  return (
    <div className="aal-timeline-rich-stack">
      {stepSubtitle ? <div className="aal-hero-compact-subtitle">{stepSubtitle}</div> : null}
      {assistLine ? (
        <div className="aal-hero-compact-line aal-hero-compact-line--assist">
          <span className="material-icons aal-hero-compact-icon" aria-hidden>
            lightbulb_outline
          </span>
          <span className="aal-hero-compact-line-text">{assistLine}</span>
        </div>
      ) : null}
      {headsUpItems.map((h, hi) => {
        const ht = (h.title ?? '').trim();
        const hh = (h.hint ?? '').trim();
        const tip = hh || ht || '';
        const label = ht || hh || 'Heads-up';
        return (
          <div
            key={hi}
            className="aal-hero-compact-line aal-hero-compact-line--heads-up"
            title={tip}
            aria-label={tip || label}
          >
            <span className="material-icons aal-hero-compact-icon" aria-hidden>
              info_outline
            </span>
            <span className="aal-hero-compact-line-text">{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function timelineActionHasRichBody(a: LiveAssistTimelineAction): boolean {
  const stepSubtitle = (a.stepSubtitle ?? '').trim();
  const assistText = (a.assistText ?? '').trim();
  const headsUp = (a.headsUp ?? []).length > 0;
  if (isTimelineCompletedStatus(a.status)) {
    return Boolean(stepSubtitle);
  }
  return Boolean(stepSubtitle || assistText || headsUp);
}

function TimelineActionRow({
  action: a,
  timelineIndex,
}: {
  action: LiveAssistTimelineAction;
  timelineIndex: number;
}) {
  const violations = a.violations ?? [];
  const constraintViolations = violations.filter(isMissedTypeViolation);
  const otherViolations = violations.filter((v) => !isMissedTypeViolation(v));
  const scriptKeys = a.scriptConstraints ?? [];
  const subflowLbl = subflowCornerLabelFromName(a.subflowName);
  const showMetaChips = scriptKeys.length > 0 || constraintViolations.length > 0;
  const richBody = timelineActionHasRichBody(a);

  return (
    <div
      data-aal-idx={timelineIndex >= 0 ? timelineIndex : undefined}
      className={`live-assist-timeline-action aal-timeline-step status-${a.status}${
        subflowLbl ? ' aal-timeline-step--has-subflow' : ''
      }${richBody ? ' aal-timeline-step--expanded' : ''}`}
    >
      {subflowLbl && (
        <div className="aal-step-subflow-badge" title={subflowLbl}>
          {subflowLbl}
        </div>
      )}
      <span className="live-assist-action-indicator">
        {a.status.startsWith('skipped_') ? (
          <span className="material-icons live-assist-skipped-warn-icon" aria-hidden>
            warning
          </span>
        ) : (
          timelineIndicatorForAction(a)
        )}
      </span>
      <div className="live-assist-action-body">
        <div className="live-assist-action-title-row">
          <span className="live-assist-action-step">
            {formatStepLabel((a.displayTitle ?? a.step) || '')}
          </span>
          {showMetaChips && (
            <div className="aal-step-meta-chips" aria-label="Script constraints">
              {scriptKeys.map((key) => {
                const { text, title } = scriptConstraintChipMeta(key);
                return (
                  <span key={key} className="aal-step-chip" title={title}>
                    {text}
                  </span>
                );
              })}
              {constraintViolations.length > 0 && (
                <span
                  className="aal-step-chip aal-step-chip--warn"
                  title={constraintViolations.map((v) => v.message).join(' · ')}
                >
                  !
                </span>
              )}
            </div>
          )}
        </div>
        {richBody ? <TimelineStepRichBody action={a} /> : null}
        {!richBody && a.remark ? (
          <span className="live-assist-action-remark">{a.remark}</span>
        ) : null}
        {otherViolations.length > 0 && (
          <div className="live-assist-action-violations">
            {otherViolations.map((v, vi) => (
              <div key={vi} className="live-assist-violation-line" title={v.message}>
                <span className="live-assist-violation-icon" aria-hidden>
                  !
                </span>
                <span className="live-assist-violation-text">{v.violationType.replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const AgentAssistLiveCard: React.FC<AgentAssistLiveCardProps> = ({
  allTurns,
  currentTurnNumber,
  heroVariant = 'default',
}) => {
  const heroCompact = heroVariant === 'compact';
  const activeAnchorRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const lastScrollSigRef = useRef<string>('');

  const currentTurn = useMemo(() => {
    return (
      allTurns.find((t) => t.turn_number === currentTurnNumber) ??
      (allTurns.length > 0 ? allTurns[allTurns.length - 1] : null)
    );
  }, [allTurns, currentTurnNumber]);

  const card = currentTurn?.agent_card ?? null;
  const ordered = card?.state_actions_ordered;
  const orderedForCurrentState = useMemo(
    () => filterOrderedForLiveLayout(ordered, currentTurn),
    [ordered, currentTurn]
  );

  const { sections, actions: timelineActions, runs } = useMemo(
    () => buildAssistTimeline(allTurns, currentTurnNumber),
    [allTurns, currentTurnNumber]
  );

  const keyToTimelineIndex = useMemo(() => {
    const m = new Map<string, number>();
    timelineActions.forEach((a, i) => m.set(a.key, i));
    return m;
  }, [timelineActions]);

  /** Latest DB status per action row (for pending pile count). */
  const latestOrderedStatusByRowKey = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of allTurns) {
      if (t.turn_number > currentTurnNumber) break;
      for (const item of t.agent_card?.state_actions_ordered ?? []) {
        m.set(orderedRowKeyFromItem(item), (item.status ?? 'not_started').toLowerCase());
      }
    }
    return m;
  }, [allTurns, currentTurnNumber]);

  const pileCounts = useMemo(() => {
    const completed = timelineActions.filter((a) => a.status.startsWith('completed')).length;
    const pending = timelineActions.filter((a) => {
      if (a.status.startsWith('skipped_')) return false;
      const rk = a.rowKey;
      if (rk && latestOrderedStatusByRowKey.has(rk)) {
        return isOrderedPendingPileItem({ status: latestOrderedStatusByRowKey.get(rk) });
      }
      return (
        a.status === 'next' || a.status === 'live' || a.status.startsWith('missed')
      );
    }).length;
    const missed = timelineActions.filter((a) => countsTowardMissedPile(a.status)).length;
    const violations = timelineActions.filter((a) =>
      (a.violations ?? []).some((v) => !isMissedTypeViolation(v))
    ).length;
    return { completed, pending, missed, violations };
  }, [timelineActions, latestOrderedStatusByRowKey]);

  const layout = useMemo(() => {
    if (sections.length === 0) {
      return {
        pastSections: [] as typeof sections,
        lastSection: null as (typeof sections)[0] | null,
        completedTop: [] as LiveAssistTimelineAction[],
        hero: [] as LiveAssistTimelineAction[],
        pendingBottom: [] as LiveAssistTimelineAction[],
      };
    }
    const pastSections = sections.slice(0, -1);
    const lastSection = sections[sections.length - 1];
    const focusStepId = (currentTurn?.focus_step_id ?? '').trim();
    const isPastState = Boolean(
      currentTurn && currentTurn.turn_number < currentTurnNumber
    );
    const layoutRowOpts = { focusStepId, isPastState };

    const actionByRowKey = new Map<string, LiveAssistTimelineAction>();
    for (const item of orderedForCurrentState ?? []) {
      const built = timelineActionFromOrderedItem(item, layoutRowOpts);
      if (built) actionByRowKey.set(orderedRowKeyFromItem(item), built);
    }
    for (const a of lastSection.actions) {
      const rk = a.rowKey ?? a.step.toLowerCase();
      if (!actionByRowKey.has(rk)) actionByRowKey.set(rk, a);
    }

    const openStepKeys = openStepKeysInOrdered(orderedForCurrentState);
    const completedTop: LiveAssistTimelineAction[] = [];
    if (orderedForCurrentState?.length) {
      for (const item of orderedForCurrentState) {
        const st = (item.status ?? '').toLowerCase();
        if (st !== 'completed' && st !== 'failed') continue;
        const canon = canonicalStepKeyFromOrdered(item);
        if (canon && openStepKeys.has(canon)) continue;
        const a = actionByRowKey.get(orderedRowKeyFromItem(item));
        if (a) completedTop.push(a);
      }
    } else {
      completedTop.push(
        ...lastSection.actions.filter((a) => a.status.startsWith('completed'))
      );
    }

    const interactionHeroes = buildCustomerInteractionHeroActions(
      orderedForCurrentState,
      actionByRowKey
    );

    let hero: LiveAssistTimelineAction[] = [];
    if (interactionHeroes.length > 0) {
      hero = interactionHeroes;
    } else if (focusStepId && orderedForCurrentState?.length) {
      const focusRow = orderedForCurrentState.find(
        (o) => String(o.step_instance_id ?? '').trim() === focusStepId
      );
      const st = (focusRow?.status ?? '').toLowerCase();
      if (
        focusRow &&
        isDialogueFocusStep(focusRow) &&
        st !== 'completed' &&
        st !== 'failed'
      ) {
        const a = actionByRowKey.get(orderedRowKeyFromItem(focusRow));
        if (a) hero = [a];
      }
    }
    if (hero.length === 0 && orderedForCurrentState?.length) {
      const firstOpen = orderedForCurrentState.find((item) => {
        if (!isDialogueFocusStep(item)) return false;
        const st = (item.status ?? '').toLowerCase();
        if (st === 'completed' || st === 'failed') return false;
        return Boolean(actionByRowKey.get(orderedRowKeyFromItem(item)));
      });
      if (firstOpen) {
        const a = actionByRowKey.get(orderedRowKeyFromItem(firstOpen));
        if (a) hero = [a];
      }
    }
    if (hero.length === 0) {
      const fallback = lastSection.actions.find(
        (a) =>
          a.status === 'next' ||
          a.status === 'pending' ||
          a.status.startsWith('missed') ||
          a.status.startsWith('skipped_')
      );
      if (fallback) hero = [fallback];
    }

    const heroRowKeys = new Set(
      hero.map((h) => h.rowKey ?? orderedRowKeyFromItem({ step_id: h.step }))
    );
    const pendingBottom: LiveAssistTimelineAction[] = buildSortedRemainingPairs(
      orderedForCurrentState,
      actionByRowKey,
      heroRowKeys,
      layoutRowOpts
    ).map((p) => p.action);

    return {
      pastSections,
      lastSection,
      completedTop,
      hero,
      pendingBottom,
    };
  }, [sections, runs, allTurns, currentTurnNumber, orderedForCurrentState, currentTurn]);

  const scrollToPile = useCallback(
    (category: 'completed' | 'pending' | 'missed' | 'violations') => {
      if (category === 'pending') {
        const focusEl = activeAnchorRef.current;
        if (focusEl) {
          requestAnimationFrame(() => {
            focusEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        }
        return;
      }
      if (category === 'missed') {
        for (const a of [...layout.hero, ...layout.pendingBottom]) {
          if (!a.status.startsWith('skipped_')) continue;
          const idx = keyToTimelineIndex.get(a.key);
          if (idx === undefined || !scrollRef.current) continue;
          const el = scrollRef.current.querySelector<HTMLElement>(`[data-aal-idx="${idx}"]`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            return;
          }
        }
      }
      const idx = findLastTimelineIndex(timelineActions, category);
      if (idx < 0 || !scrollRef.current) return;
      const el = scrollRef.current.querySelector<HTMLElement>(`[data-aal-idx="${idx}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    },
    [timelineActions, keyToTimelineIndex, layout.hero, layout.pendingBottom]
  );

  const entityRowsRaw = useMemo(() => buildEntityRows(card), [card]);
  const sortedEntities = useMemo(
    () => sortEntitiesByRecency(allTurns, currentTurnNumber, entityRowsRaw),
    [allTurns, currentTurnNumber, entityRowsRaw]
  );

  const updatedEntitySet = useMemo(() => {
    const prevTurn = allTurns.find((t) => t.turn_number === currentTurnNumber - 1) ?? null;
    const prevCard = prevTurn?.agent_card ?? null;
    const completedStatus = (s: string | null | undefined) => (s ?? '').toLowerCase() === 'completed';
    const isFilled = (e: StateEntityFullItem) =>
      completedStatus(e.status) || e.filled === true;
    const entityKey = (e: StateEntityFullItem) => String(e.entity_name ?? '').trim();
    const currFilled = (card?.state_entities_full ?? []).filter(isFilled).map(entityKey).filter(Boolean);
    const prevFilled = (prevCard?.state_entities_full ?? []).filter(isFilled).map(entityKey).filter(Boolean);
    const prevSet = new Set(prevFilled);
    const names = new Set<string>();
    for (const n of currFilled) {
      if (!prevSet.has(n)) names.add(n);
    }
    for (const e of card?.state_entities_full ?? []) {
      if (e.is_changed && entityKey(e)) names.add(entityKey(e));
    }
    return names;
  }, [allTurns, currentTurnNumber, card]);

  const ongoingLines = useMemo(() => gatherOngoingListenLines(card, currentTurn), [card, currentTurn]);

  const ongoingByLiveStep = useMemo(() => {
    const liveHeroes = layout.hero.filter((h) => h.status === 'live');
    return assignOngoingToLiveSteps(liveHeroes, ongoingLines);
  }, [layout.hero, ongoingLines]);

  const scrollSig = useMemo(() => {
    const h = layout.hero.map((x) => x.step).join('|');
    return `${currentTurnNumber}:${h}`;
  }, [currentTurnNumber, layout.hero]);

  useEffect(() => {
    if (scrollSig === lastScrollSigRef.current) return;
    lastScrollSigRef.current = scrollSig;
    const el = activeAnchorRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [scrollSig]);

  if (allTurns.length === 0 || !card) {
    return (
      <div className="aal-card">
        <div className="live-assist-card-piles">
          <button type="button" className="live-assist-pile pile-completed" disabled>
            Completed 0
          </button>
          <button type="button" className="live-assist-pile pile-pending" disabled>
            Pending 0
          </button>
          <button type="button" className="live-assist-pile pile-missed" disabled>
            Missed 0
          </button>
          <button type="button" className="live-assist-pile pile-violations" disabled>
            Violations 0
          </button>
        </div>
        <div className="aal-empty">No agent assist data for this moment.</div>
      </div>
    );
  }

  const stateTitle = layout.lastSection?.state.stateTitle ?? '';
  const nonIntentSignals =
    card.signals?.filter(
      (signal) =>
        signal.value &&
        typeof signal.value === 'string' &&
        signal.value.trim().length > 0 &&
        signal.value.toLowerCase() !== 'none' &&
        signal.type !== 'intent'
    ) ?? [];
  const intentSignal = card.signals?.find(
    (s) =>
      s.type === 'intent' &&
      s.value &&
      typeof s.value === 'string' &&
      s.value.trim().length > 0 &&
      s.value.toLowerCase() !== 'none'
  );

  return (
    <div className={`aal-card${heroCompact ? ' aal-card--focus-compact' : ''}`}>
      <div className="live-assist-card-piles">
        <button
          type="button"
          className="live-assist-pile pile-completed"
          onClick={() => scrollToPile('completed')}
        >
          Completed {pileCounts.completed}
        </button>
        <button type="button" className="live-assist-pile pile-pending" onClick={() => scrollToPile('pending')}>
          Pending {pileCounts.pending}
        </button>
        <button type="button" className="live-assist-pile pile-missed" onClick={() => scrollToPile('missed')}>
          Missed {pileCounts.missed}
        </button>
        <button
          type="button"
          className="live-assist-pile pile-violations"
          onClick={() => scrollToPile('violations')}
        >
          Violations {pileCounts.violations}
        </button>
      </div>

      <div className="aal-main">
        <div className="aal-steps-column">
          <div className="aal-scroll" ref={scrollRef}>
            {layout.pastSections.map((sec) => (
              <div key={sec.state.key} className="aal-state-section live-assist-timeline-section">
                <div className="live-assist-timeline-state-marker aal-state-sticky-head">
                  {formatStepLabel(sec.state.stateTitle)}
                </div>
                {sec.actions.map((a) => (
                  <TimelineActionRow
                    key={a.key}
                    action={a}
                    timelineIndex={keyToTimelineIndex.get(a.key) ?? -1}
                  />
                ))}
              </div>
            ))}

            {(layout.completedTop.length > 0 ||
              layout.hero.length > 0 ||
              layout.pendingBottom.length > 0) && (
              <div className="aal-state-section aal-state-section--current">
                {(stateTitle ?? '').trim().length > 0 && (
                  <div className="live-assist-timeline-state-marker aal-state-sticky-head aal-state-sticky-head--live">
                    <span className="aal-state-sticky-title">{formatStepLabel(stateTitle)}</span>
                    <span className="aal-state-live-pill">Live</span>
                  </div>
                )}

                {layout.completedTop.length > 0 && (
                  <>
                    <div className="aal-zone-label">Done</div>
                    {layout.completedTop.map((a) => (
                      <TimelineActionRow
                        key={`done-${a.key}`}
                        action={a}
                        timelineIndex={keyToTimelineIndex.get(a.key) ?? -1}
                      />
                    ))}
                  </>
                )}

                <div ref={activeAnchorRef} className="aal-hero-wrap">
              {/* <div className="aal-zone-label">Focus</div> */}
              {layout.hero.length === 0 && (
                <div className="aal-hero">
                  <div className="aal-hero-title">No active step</div>
                  <p className="aal-hero-remark">Awaiting workflow signal or next scripted action.</p>
                </div>
              )}
              {layout.hero.map((a) => {
                const oi = findOrderedItemForAction(orderedForCurrentState, a);
                const ti = keyToTimelineIndex.get(a.key);
                if (heroCompact) {
                  return (
                    <FocusHeroStepCompact
                      key={`hero-${a.key}`}
                      action={a}
                      orderedItem={oi}
                      ongoingByLiveStep={ongoingByLiveStep}
                      timelineIndex={ti}
                    />
                  );
                }
                const badge = heroSubflowCornerLabel(oi);
                const assistText = (oi?.assist_text ?? '').trim();
                const stepSubtitle = (oi?.step_subtitle ?? '').trim();
                /** Single coaching line: prefer assist when present (and not redundant with subtitle); else generated listen line. */
                const assistForGuidance =
                  assistText && assistText !== stepSubtitle ? assistText : '';
                const generatedListen =
                  a.status === 'live'
                    ? ongoingByLiveStep.get(a.step.toLowerCase())?.trim() || ''
                    : '';
                const guidanceLine = assistForGuidance || generatedListen;
                const headsUpItems =
                  Array.isArray(oi?.heads_up) && oi
                    ? oi.heads_up.filter(
                        (h) =>
                          (h?.title ?? '').trim().length > 0 || (h?.hint ?? '').trim().length > 0
                      )
                    : [];
                const entRows = heroEntityRows(card, oi, a.step);
                return (
                  <div
                    key={`hero-${a.key}`}
                    data-aal-idx={ti !== undefined ? ti : undefined}
                    className={`aal-hero status-${a.status}`}
                  >
                    {badge && <div className="aal-hero-badge">{badge}</div>}
                    <div className="aal-hero-title">
                      <FocusStepLiveIndicator action={a} orderedItem={oi} />
                      {a.status.startsWith('missed') && (
                        <span className="aal-missed-indicator" title="Missed step">
                          {timelineIndicatorForAction(a)}
                        </span>
                      )}
                      {a.status.startsWith('skipped_') && (
                        <span
                          className="material-icons aal-skipped-warn-icon"
                          title="Skipped — still recover in this state"
                          aria-hidden
                        >
                          warning
                        </span>
                      )}
                      {formatStepLabel((a.displayTitle ?? a.step) || '')}
                    </div>
                    {stepSubtitle && (
                      <div className="aal-hero-subtitle">{stepSubtitle}</div>
                    )}
                    {guidanceLine && (
                      <div className="aal-hero-guidance">{guidanceLine}</div>
                    )}
                    {headsUpItems.length > 0 && (
                      <div className="aal-hero-heads-up" aria-label="Heads-up">
                        {headsUpItems.map((h, hi) => {
                          const ht = (h.title ?? '').trim();
                          const hh = (h.hint ?? '').trim();
                          const tip = hh || ht || '';
                          const label = ht || hh || 'Heads-up';
                          return (
                            <div
                              key={hi}
                              className="aal-hero-heads-up-tile"
                              title={tip}
                              aria-label={tip || label}
                            >
                              <span className="aal-hero-heads-up-label">{label}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {a.remark && <div className="aal-hero-remark">{a.remark}</div>}
                    {(a.violations?.length ?? 0) > 0 && (
                      <div className="live-assist-action-violations aal-hero-violations">
                        {a.violations!.map((v, vi) => (
                          <div key={vi} className="live-assist-violation-line" title={v.message}>
                            <span
                              className={`live-assist-violation-icon ${isMissedTypeViolation(v) ? 'live-assist-violation-icon-missed' : ''}`}
                              aria-hidden
                            >
                              {isMissedTypeViolation(v) ? '✗' : '!'}
                            </span>
                            <span className="live-assist-violation-text">{v.violationType.replace(/_/g, ' ')}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {entRows.length > 0 && (
                      <div className="aal-hero-entities">
                        {entRows.map((er) => (
                          <div key={er.name} className="aal-hero-entity-row">
                            <span className="aal-hero-entity-k">{formatStepLabel(er.name)}</span>
                            <span className="aal-hero-entity-v">{er.value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {layout.pendingBottom.length > 0 && (
              <>
                <div className="aal-zone-label">Upcoming</div>
                {layout.pendingBottom.map((a) => (
                  <TimelineActionRow
                    key={`pend-${a.key}`}
                    action={a}
                    timelineIndex={keyToTimelineIndex.get(a.key) ?? -1}
                  />
                ))}
              </>
            )}
              </div>
            )}
          </div>

          <div
            className={`aal-footer-meta${
              (card.violations ?? []).filter((t) => String(t ?? '').trim()).length > 0
                ? ' aal-footer-meta--has-violations-ticker'
                : ''
            }`}
          >
            {(nonIntentSignals.length > 0 || intentSignal) && (
              <div className="aal-signals">
                {intentSignal && (
                  <span className="aal-signal-pill" title="Detected intent">
                    Intent: {formatStepLabel(intentSignal.value)}
                  </span>
                )}
                {nonIntentSignals.map((signal, index) => (
                  <span key={`${signal.type}-${index}`} className="aal-signal-pill">
                    {formatStepLabel(signal.value)}
                  </span>
                ))}
              </div>
            )}
            {(card.violations ?? []).length > 0 && (
              <ViolationsTicker lines={(card.violations ?? []).map((t) => String(t ?? '').trim()).filter(Boolean)} />
            )}
          </div>
        </div>

        {sortedEntities.length > 0 && (
          <div className="aal-entities-column">
            <div className="aal-entities-header">Entities</div>
            <div className="aal-entities-scroll">
              {sortedEntities.map((e) => (
                <div
                  key={e.entity}
                  className={`aal-entity-line entity-${e.status}${
                    updatedEntitySet.has(e.entity) ? ' aal-entity-line-updated' : ''
                  }`}
                >
                  <span className="aal-entity-k">{formatStepLabel(e.entity)}</span>
                  {e.status === 'not_filled' ? (
                    <span className="aal-entity-v">· · ·</span>
                  ) : (
                    <EntityValueCell value={e.value} valueClassName="aal-entity-v" />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {card.attention && card.attention.length > 0 && (
        <div className="aal-footer-meta" style={{ borderTop: '1px solid #fecaca', background: '#fff7f7' }}>
          {card.attention.map((w, i) => (
            <div key={`att-${i}`} style={{ color: '#991b1b', fontSize: '0.8rem' }}>
              {w}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AgentAssistLiveCard;
